/**
 * La traduction du fil, vue du cockpit — armée quand elle sert, muette sinon.
 *
 * Une seule question décide de tout : **le fan écrit-il dans une autre langue
 * que le chatteur ?** Si non — le cas ordinaire — rien ne s'affiche, rien ne
 * s'appelle, et le composeur se comporte exactement comme avant. C'est ce qui
 * rend ce chantier sans risque pour les conversations françaises, qui sont
 * l'écrasante majorité.
 *
 * 🪤 **La langue du chatteur, c'est `i18n.resolvedLanguage`, pas `language`.**
 * `language` peut valoir `fr-CA` (ce que dit le navigateur) alors que la locale
 * réellement chargée est `fr`. Le backend n'accepte que deux lettres ; envoyer
 * `fr-CA` ferait rendre 422 à chaque frappe, et l'écran n'aurait aucune raison
 * visible de rester vide.
 */

import { useQuery } from "@tanstack/react-query";
import { atomWithStorage } from "jotai/utils";
import { useAtom } from "jotai";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiError } from "@/core/api/client";

import {
  lireLaLangueDuFan,
  traduireLesEntrants,
  type SourceDeLangue,
  type TexteTraduit,
  traduireLeTexte,
} from "./api";

/**
 * Combien d'entrants on demande d'un coup.
 *
 * ⚠️ **L'autorité est le serveur**, pas cette ligne : `MessagesIn.ids` est
 * borné à 60 par pydantic (`app/api/routes/traduction.py`). Le dépasser ici ne
 * produirait pas une liste tronquée mais un 422, et comme la requête ne
 * réessaie pas, la traduction disparaîtrait de l'écran sans rien dire. Ce
 * nombre doit rester ≤ celui de la route.
 *
 * 🪶 Rien n'est perdu au-delà : on prend les 60 PLUS RÉCENTS, et le rendu
 * suivant redemande les précédents — le fil se traduit par tranches en
 * remontant, au rythme où le chatteur le lit.
 */
const MAX_PAR_APPEL = 60;

/**
 * Combien de temps après la dernière frappe l'aperçu part au traducteur.
 *
 * Assez long pour ne pas traduire chaque syllabe, assez court pour que la
 * traduction soit là quand le chatteur relit sa phrase avant Entrée. Le
 * traducteur met ~1,5 s (mesuré le 16-09-2026) : à 600 ms de pause, le fan
 * lit la traduction ~2 s après le dernier caractère tapé.
 */
const PAUSE_AVANT_TRADUCTION_MS = 600;

/**
 * Traduction ÉTEINTE, conversation par conversation.
 *
 * Le défaut est ALLUMÉ : un chatteur qui ouvre un fan étranger veut le lire,
 * pas cliquer d'abord. On mémorise donc l'exception (« celle-là, je la lis en
 * VO »), pas la règle — mémoriser la règle obligerait chaque nouveau chatteur
 * à découvrir un bouton pour que l'outil serve à quelque chose.
 */
export const traductionEteinteAtom = atomWithStorage<Record<string, boolean>>(
  "cockpit:traduction-eteinte",
  {},
);

/** La locale d'interface, ramenée aux deux lettres que le backend accepte. */
export function useLangueDuChatteur(): string {
  const { i18n } = useTranslation();
  return langueDuChatteur(i18n);
}

/** La même, hors d'un composant (le flux SSE de la liste). `i18n` absent :
 *  les tests qui simulent `useTranslation` sans lui. */
export function langueDuChatteur(
  i18n: { resolvedLanguage?: string; language?: string } | undefined,
): string {
  const brut = i18n?.resolvedLanguage || i18n?.language || "en";
  return brut.slice(0, 2).toLowerCase();
}

export interface LangueResolue {
  /** `null` tant que le serveur ne sait pas. */
  langue: string | null;
  /** D'où il la tient. `null` quand il ne sait pas, ou backend d'avant. */
  source: SourceDeLangue | null;
}

/**
 * Dans quelle langue ce fan écrit — `null` tant que le serveur ne sait pas.
 *
 * `dernierEntrant` est l'id du message du fan le plus récent à l'écran. À
 * chaque nouvel entrant, la langue est REDEMANDÉE — le serveur la lit dans ce
 * que le fan a écrit, et chaque message peut la changer : un fan qui n'avait
 * qu'un « hi » (langue prise sur son Telegram) vient peut-être d'écrire sa
 * première vraie phrase, et c'est elle qui compte désormais. Sans ça, un fan
 * étranger restait lu en VO pendant dix minutes (le `staleTime`), et le
 * chatteur lui répondait à l'aveugle. Un aller-réseau par message reçu, sur
 * une route qui ne fait qu'une requête et un `langdetect`.
 */
export function useLangueDuFan(
  conversationId: string | undefined,
  dernierEntrant?: string,
): LangueResolue {
  const { data, refetch } = useQuery({
    queryKey: ["traduction", "langue", conversationId],
    queryFn: () => lireLaLangueDuFan(conversationId as string),
    enabled: Boolean(conversationId),
    // La langue d'un fan ne change pas d'une minute à l'autre. On la relit à
    // l'ouverture d'une conversation, et à chaque entrant (ci-dessous).
    staleTime: 10 * 60_000,
    refetchOnWindowFocus: false,
    // Un backend qui ne connaît pas encore la route rend 404 : la traduction
    // reste simplement éteinte. C'est ce qui permet de déployer le cockpit
    // AVANT Cyrano sans que rien ne casse à l'écran.
    retry: false,
  });

  const entrantPrecedent = useRef(dernierEntrant);
  useEffect(() => {
    if (entrantPrecedent.current === dernierEntrant) return;
    entrantPrecedent.current = dernierEntrant;
    if (data) void refetch();
  }, [dernierEntrant, data, refetch]);

  return { langue: data?.langue ?? null, source: data?.source ?? null };
}

export interface EtatDeTraduction {
  /** Vrai quand il y a vraiment une barrière de langue à franchir. */
  active: boolean;
  langueDuFan: string | null;
  /** D'où vient `langueDuFan` : `telegram` = la locale de son appareil, à
   *  défaut de texte — le bandeau le dit, parce qu'elle peut se tromper. */
  sourceDeLangue: SourceDeLangue | null;
  langueDuChatteur: string;
  /** id de message → texte traduit. Vide tant que rien n'est revenu. */
  traductions: Map<string, string>;
  /** Les entrants dont la traduction est DEMANDÉE et pas encore revenue. La
   *  bulle respire au lieu de montrer l'original (Kayla, 26-09-2026). Vide
   *  dès que le lot répond — y compris pour les ids que le serveur n'a pas
   *  rendus (rien à traduire) : ceux-là redeviennent des bulles ordinaires. */
  enAttente: ReadonlySet<string>;
  /** Le chatteur a éteint la traduction sur cette conversation. */
  eteinte: boolean;
  basculer: () => void;
}

/**
 * L'état de traduction d'une conversation, et les entrants déjà traduits.
 *
 * `idsEntrants` est la liste des messages ENTRANTS actuellement à l'écran. On
 * ne demande que ce qui s'affiche : un fil de mille messages ne se traduit pas
 * d'un coup, et le chatteur qui remonte redemande au fur et à mesure.
 *
 * `servies` (26-09-2026) : ce que le serveur a DÉJÀ traduit, par id puis par
 * langue — Cyrano traduit à la persistance et le message arrive avec sa
 * traduction (`ApiMessage.traductions`). Un id servi dans la langue du
 * chatteur n'est jamais redemandé : sur un fil courant, aucun appel ne part,
 * et rien n'attend.
 */
export function useTraductionDuFil(
  conversationId: string | undefined,
  idsEntrants: readonly string[],
  servies: ReadonlyMap<string, Readonly<Record<string, string>>> = SANS_SERVIES,
  /** Le dernier message du FAN, qui fait relire sa langue. Absent = le dernier
   *  id de la liste — quand la liste ne porte que des entrants. */
  dernierEntrant: string | undefined = idsEntrants[idsEntrants.length - 1],
): EtatDeTraduction {
  const langueDuChatteur = useLangueDuChatteur();
  const { langue: langueDuFan, source: sourceDeLangue } = useLangueDuFan(
    conversationId,
    dernierEntrant,
  );
  const [eteintes, setEteintes] = useAtom(traductionEteinteAtom);

  const eteinte = Boolean(conversationId && eteintes[conversationId]);
  const active =
    Boolean(conversationId) &&
    Boolean(langueDuFan) &&
    langueDuFan !== langueDuChatteur &&
    !eteinte;

  const basculer = useCallback(() => {
    if (!conversationId) return;
    setEteintes((prev) => {
      const suivant = { ...prev };
      if (suivant[conversationId]) delete suivant[conversationId];
      else suivant[conversationId] = true;
      return suivant;
    });
  }, [conversationId, setEteintes]);

  // Ce qu'on a déjà, et ce qu'il reste à demander. Le cache vit ici plutôt que
  // dans react-query : la clé serait la LISTE des ids, qui change à chaque
  // message reçu — on repaierait tout le fil pour un message de plus.
  const deja = useRef(new Map<string, string>());
  const demandes = useRef(new Set<string>());
  const conversationPrecedente = useRef<string | undefined>(undefined);
  if (conversationPrecedente.current !== conversationId) {
    conversationPrecedente.current = conversationId;
    deja.current = new Map();
    demandes.current = new Set();
  }

  const manquants = useMemo(
    () =>
      active
        ? idsEntrants
            .filter(
              (id) =>
                !deja.current.has(id) &&
                !demandes.current.has(id) &&
                !servies.get(id)?.[langueDuChatteur],
            )
            .slice(-MAX_PAR_APPEL)
        : [],
    [active, idsEntrants, servies, langueDuChatteur],
  );

  // Une clé qui ne bouge que quand il reste vraiment quelque chose à demander.
  const cle = manquants.join(",");

  const { data, isError } = useQuery({
    queryKey: ["traduction", "entrants", conversationId, langueDuChatteur, cle],
    queryFn: async () => {
      manquants.forEach((id) => demandes.current.add(id));
      return traduireLesEntrants(conversationId as string, {
        ids: manquants,
        langue_du_chatteur: langueDuChatteur,
      });
    },
    enabled: active && manquants.length > 0,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  });

  // 🩸 Le lot est rangé PENDANT le rendu, pas dans un `useEffect`.
  //
  // Il l'était, et c'était un bug muet : un effet tourne APRÈS le rendu, donc
  // le `useMemo` qui construit la Map voyait encore le cache vide au moment
  // précis où `data` arrivait — et comme `data` ne rebouge plus ensuite, aucun
  // rendu suivant ne venait rattraper. Les traductions arrivaient du serveur,
  // étaient rangées, et n'atteignaient jamais l'écran. Rien ne cassait : le
  // chatteur voyait simplement les originaux, comme si le fan écrivait sa
  // langue. Attrapé par `traductionDuFil.test.tsx`, jamais par le typecheck.
  //
  // Écrire dans une ref pendant le rendu est ici sans danger : l'opération est
  // idempotente (mêmes clés, mêmes valeurs), donc un double rendu StrictMode
  // ne produit rien de différent.
  const traductions = useMemo(() => {
    if (data) {
      for (const [id, texte] of Object.entries(data.traductions)) {
        deja.current.set(id, texte);
      }
    }
    const rendu = new Map(deja.current);
    // Ce que le serveur a servi avec le message passe devant ce qu'on a
    // demandé : c'est la même traduction, mémorisée au même endroit.
    for (const [id, parLangue] of servies) {
      const texte = parLangue[langueDuChatteur];
      if (texte) rendu.set(id, texte);
    }
    return rendu;
  }, [data, servies, langueDuChatteur]);

  // Demandé, pas revenu : `data` est `undefined` tant que la requête de CETTE
  // clé n'a pas répondu (pas de `placeholderData`), et un échec la vide pour
  // de bon — les bulles retombent alors sur l'original, jamais sur du vide.
  const enAttente = useMemo<ReadonlySet<string>>(
    () =>
      active && manquants.length > 0 && data === undefined && !isError
        ? new Set(manquants)
        : SANS_ATTENTE,
    [active, manquants, data, isError],
  );

  return {
    active,
    langueDuFan,
    sourceDeLangue,
    langueDuChatteur,
    traductions,
    enAttente,
    eteinte,
    basculer,
  };
}

const SANS_SERVIES: ReadonlyMap<string, Readonly<Record<string, string>>> = new Map();
const SANS_ATTENTE: ReadonlySet<string> = new Set();

/** Traduit le message que le chatteur s'apprête à envoyer. N'envoie RIEN. */
export function useTraduireAvantEnvoi(conversationId: string | undefined) {
  const langueDuChatteur = useLangueDuChatteur();
  return useCallback(
    (texte: string): Promise<TexteTraduit> =>
      traduireLeTexte(conversationId as string, {
        texte,
        sens: "envoyer",
        langue_du_chatteur: langueDuChatteur,
      }),
    [conversationId, langueDuChatteur],
  );
}

export interface ApercuDeTraduction {
  /** La traduction du texte ACTUEL du composeur — `null` si le texte est
   *  vide, ou si elle n'est pas encore revenue. Jamais celle d'un texte
   *  d'avant : un aperçu périmé est pire que pas d'aperçu. */
  apercu: TexteTraduit | null;
  enCours: boolean;
  erreur: string | null;
  /** La traduction de `texte`, tout de suite si l'aperçu la porte déjà, sinon
   *  demandée au serveur. C'est ce que l'envoi appelle : un seul geste, et
   *  pas de seconde attente quand la traduction est déjà à l'écran. */
  traduireMaintenant: (texte: string) => Promise<TexteTraduit>;
}

/**
 * La traduction qui suit la frappe.
 *
 * Kayla, 16-09-2026 : « beaucoup plus fluide, rapide ». Avant, Entrée ouvrait
 * une barre, le chatteur attendait le modèle (5,5 s avec l'ancien), relisait,
 * et cliquait une seconde fois. Ici la traduction part pendant qu'il tape —
 * `PAUSE_AVANT_TRADUCTION_MS` après la dernière touche — et Entrée envoie ce
 * qui est déjà à l'écran.
 *
 * 🪤 **Un aperçu ne vaut que pour SON texte.** Chaque réponse est comparée au
 * texte tapé au moment où elle revient ; une réponse pour un texte déjà
 * modifié est jetée. Sans ça, une traduction lente d'une phrase d'avant
 * s'afficherait sous la phrase d'après — et partirait avec elle.
 *
 * Les traductions obtenues sont gardées par texte, le temps de la
 * conversation : effacer puis retaper la même phrase ne repaie rien, et
 * l'envoi retrouve instantanément ce que l'aperçu avait montré.
 */
export function useApercuDeTraduction(
  conversationId: string | undefined,
  texte: string,
  actif: boolean,
): ApercuDeTraduction {
  const traduire = useTraduireAvantEnvoi(conversationId);
  const { t } = useTranslation();
  const propre = texte.trim();

  const [obtenues, setObtenues] = useState<Map<string, TexteTraduit>>(
    () => new Map(),
  );
  const [enVol, setEnVol] = useState<string | null>(null);
  const [erreurPour, setErreurPour] = useState<{ texte: string; message: string } | null>(null);
  // Les appels en cours, par texte — pour qu'un Entrée pendant l'aperçu ne
  // lance pas un second appel identique mais attende le premier.
  const promesses = useRef(new Map<string, Promise<TexteTraduit>>());

  const conversationPrecedente = useRef(conversationId);
  if (conversationPrecedente.current !== conversationId) {
    conversationPrecedente.current = conversationId;
    promesses.current = new Map();
  }
  useEffect(() => {
    setObtenues(new Map());
    setEnVol(null);
    setErreurPour(null);
  }, [conversationId]);

  const messageDErreur = useCallback(
    (e: unknown) =>
      e instanceof ApiError && e.detail
        ? e.detail
        : t("panels.fan.traduction.relecture.erreur", {
            defaultValue:
              "La traduction n'a pas abouti — rien n'est parti. Réessaie.",
          }),
    [t],
  );

  const demander = useCallback(
    (aTraduire: string): Promise<TexteTraduit> => {
      const deja = promesses.current.get(aTraduire);
      if (deja) return deja;
      setEnVol(aTraduire);
      setErreurPour(null);
      const promesse = traduire(aTraduire)
        .then((rendu) => {
          setObtenues((prev) => {
            const suivant = new Map(prev);
            suivant.set(aTraduire, rendu);
            return suivant;
          });
          return rendu;
        })
        .catch((e: unknown) => {
          setErreurPour({ texte: aTraduire, message: messageDErreur(e) });
          throw e;
        })
        .finally(() => {
          promesses.current.delete(aTraduire);
          setEnVol((courant) => (courant === aTraduire ? null : courant));
        });
      promesses.current.set(aTraduire, promesse);
      return promesse;
    },
    [traduire, messageDErreur],
  );

  // La frappe : après la pause, on demande — sauf si on a déjà.
  useEffect(() => {
    if (!actif || !propre || obtenues.has(propre)) return;
    const minuteur = setTimeout(() => {
      demander(propre).catch(() => {
        // L'erreur est déjà posée dans `erreurPour` ; rien n'est parti.
      });
    }, PAUSE_AVANT_TRADUCTION_MS);
    return () => clearTimeout(minuteur);
  }, [actif, propre, obtenues, demander]);

  const traduireMaintenant = useCallback(
    (aTraduire: string): Promise<TexteTraduit> => {
      const t2 = aTraduire.trim();
      const deja = obtenues.get(t2);
      if (deja) return Promise.resolve(deja);
      return demander(t2);
    },
    [obtenues, demander],
  );

  const apercu = actif && propre ? (obtenues.get(propre) ?? null) : null;
  return {
    apercu,
    enCours: actif && Boolean(propre) && apercu === null && enVol === propre,
    erreur:
      actif && propre && erreurPour && erreurPour.texte === propre
        ? erreurPour.message
        : null,
    traduireMaintenant,
  };
}
